import axios, { AxiosError } from "axios"
import { API } from "../api/constants"

export type TUnauthorizedReason = "session-expired"

let unauthorizedHandler: null | ((reason: TUnauthorizedReason) => void) = null

/** Wird vom AuthContext gesetzt: meldet ab, wenn die Session abgelaufen ist. */
export const setUnauthorizedHandler = (handler: null | ((reason: TUnauthorizedReason) => void)) => {
    unauthorizedHandler = handler
}

let accountIncompleteHandler: null | (() => void) = null

/** Wird vom AuthContext gesetzt: Mitarbeiter-Login ohne verknüpften Employee (403 "Unkown user"). */
export const setAccountIncompleteHandler = (handler: null | (() => void)) => {
    accountIncompleteHandler = handler
}

// Session abgelaufen (bis zur nächsten Anmeldung): Fehler-Toasts werden dann unterdrückt, siehe services/messages.tsx
let sessionExpired = false
export const isSessionExpired = () => sessionExpired
export const markSessionExpired = () => {
    sessionExpired = true
}
export const resetSessionExpired = () => {
    sessionExpired = false
}

/** Fehler eines Requests, der wegen abgelaufener Session scheiterte. */
export type TSessionAwareError = AxiosError & { sessionExpired?: boolean }

// Schreibweise so im Backend (auth-contract.md)
const UNKNOWN_USER_MESSAGE = "Unkown user - reauthentication required"

/** Client ohne 401-Interceptor, nur für die Session-Nachprüfung (verhindert Rekursion). */
export const sessionCheckClient = axios.create({
    headers: {
        "Content-Type": "application/json",
    },
    withCredentials: true,
})

/** Alle Fach-Requests: das Session-Cookie wird per `withCredentials` mitgeschickt. */
export const authenticatedAxiosInstance = axios.create({
    headers: {
        "Content-Type": "application/json",
    },
    withCredentials: true,
})

// Anmelde-Endpunkte: ein 401 dort ist eine normale Antwort (falsches Passwort, keine Session) und löst keine Nachprüfung aus.
const AUTH_ENDPOINT = /\/auth\/(session|login|logout|password)(\/|\?|$)/
const isAuthEndpoint = (url?: string) => !!url && AUTH_ENDPOINT.test(url)

// Dedupliziert parallele Nachprüfungen: viele gleichzeitige 401 -> genau ein GET /auth/session und höchstens ein Logout.
let recheckInFlight: Promise<void> | null = null

export const recheckSessionOrLogout = (): Promise<void> => {
    if (!recheckInFlight) {
        recheckInFlight = sessionCheckClient
            .get(`${API}/auth/session`)
            .then(() => undefined)
            .catch((error: AxiosError) => {
                // Nur wenn auch /auth/session 401 liefert, ist die Session wirklich weg (Netzwerkfehler -> nicht abmelden).
                if (error?.response?.status === 401) {
                    markSessionExpired()
                    unauthorizedHandler?.("session-expired")
                }
            })
            .finally(() => {
                recheckInFlight = null
            })
    }
    return recheckInFlight
}

const isJsonResponse = (contentType: unknown) => typeof contentType === "string" && contentType.toLowerCase().includes("application/json")

/** Downloads (`responseType` blob/arraybuffer) bekommen auch Fehler als Blob: JSON-Fehlerkörper in ein Objekt umwandeln, damit `displayMessage` lesbar ist. */
export const parseBinaryErrorBody = async (error: AxiosError) => {
    const response = error?.response
    if (!response || !isJsonResponse(response.headers?.["content-type"])) return
    const data: unknown = response.data
    try {
        let text: string | undefined
        if (typeof Blob !== "undefined" && data instanceof Blob) {
            text = typeof data.text === "function" ? await data.text() : await new Response(data).text()
        } else if (Object.prototype.toString.call(data) === "[object ArrayBuffer]" || ArrayBuffer.isView(data)) {
            text = new TextDecoder().decode(data as ArrayBuffer)
        }
        if (text !== undefined) response.data = JSON.parse(text)
    } catch {
        // kein gültiges JSON: Fehlerkörper unverändert lassen
    }
}

authenticatedAxiosInstance.interceptors.response.use(
    (response) => response,
    async (error: TSessionAwareError) => {
        await parseBinaryErrorBody(error)
        const status = error?.response?.status
        if (status === 401 && !isAuthEndpoint(error?.config?.url)) {
            await recheckSessionOrLogout()
            if (sessionExpired) error.sessionExpired = true
        }
        if (status === 403 && (error?.response?.data as { message?: string } | undefined)?.message === UNKNOWN_USER_MESSAGE) {
            accountIncompleteHandler?.()
        }
        return Promise.reject(error)
    }
)
